import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Header from '../components/Header';
import Binder from '../components/Binder';
import Carousel from '../components/Carousel/Carousel';
import SearchBar from '../components/SearchBar';
import WhitePane from '../components/WhitePane';
import WhitePaneFooter from '../components/WhitePaneFooter/WhitePaneFooter';

import './HomePage.css';

const HomePage = ({ setSupertype, setType, resetListings }) => {
    const [listingsGroups, setListingsGroups] = useState([]); // Array of arrays, each representing a Binder
    const [featuredListings, setFeaturedListings] = useState([]);
    const [holidayListings, setHolidayListings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [featuredLoading, setFeaturedLoading] = useState(true);
    const [holidayLoading, setHolidayLoading] = useState(true);
    const [error, setError] = useState(null);
    const [supertype, setSupertypeState] = useState('');
    const [type, setTypeState] = useState('');
    const [page, setPage] = useState(3); // Start the "See More" button at page 3
    const [count] = useState(6); // Page size for listings
    const [totalListings, setTotalListings] = useState(0); // Total active listings
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    const fetchHolidayListings = async () => {
        try {
            const url = `${apiBaseUrl}/listings?psaOrderNumber=24398108&count=16`;

            //url += `&includeSet=false&`
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include', // Ensures cookies are sent with cross-origin requests
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }

            const result = await response.json();
            setHolidayListings(result.listings);
        } catch (error) {
            setError(error.message);
        } finally {
            setHolidayLoading(false);
        }
    };

    const fetchFeaturedListings = async () => {
        try {
            const url = `${apiBaseUrl}/listings?featured=1`;

            //url += `&includeSet=false&`
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include', // Ensures cookies are sent with cross-origin requests
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }

            const result = await response.json();
            setFeaturedListings(result.listings);
        } catch (error) {
            setError(error.message);
        } finally {
            setFeaturedLoading(false);
        }
    };

    const fetchTotalListings = async () => {
        try {
            const url = `${apiBaseUrl}/listings/count`;

            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include', // Ensures cookies are sent with cross-origin requests
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch total listings: ${response.statusText}`);
            }

            const result = await response.json();
            setTotalListings(result.Count); // Update total listings count
        } catch (error) {
            setError(error.message);
        }
    };

    const fetchListings = async (currentPage) => {
        try {
            let url = `${apiBaseUrl}/listings?`;
            //url += `includeSet=false&`
            url += `page=${currentPage}&count=${count}&`;
    
            if (supertype) {
                url += `supertype=${supertype}&`;
            }
            if (type) {
                url += `type=${type}&`;
            }
    
            url = url.slice(0, -1); // Remove trailing '&' or '?' if exists
    
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include', // Ensures cookies are sent with cross-origin requests
                headers: {
                    'Content-Type': 'application/json',
                },
            });
    
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }
    
            const result = await response.json();
    
            if (result.listings.length > 0) {
                setListingsGroups((prevGroups) => {
                    const newListings = result.listings.filter(
                        (listing) =>
                            !prevGroups.flat().some((existingListing) => existingListing.id === listing.id)
                    );
                    return [...prevGroups, newListings];
                });
    
                // Only increment the page if there are results
                setPage((prevPage) => (prevPage !== null ? prevPage + 1 : null));
            } else {
                setPage(null); // No more pages to fetch
            }
        } catch (error) {
            setError(error.message);
        }
    };
    
    

    useEffect(() => {
        const fetchAll = async () => {
            await fetchFeaturedListings();
            await fetchHolidayListings();
            await fetchListings(1);
            await fetchTotalListings();
            setPage(2);
        };
    
        setListingsGroups([]);
        setPage(3);
        fetchAll();
    }, [supertype, type]);

    // Find the first featured listing with flavor text that's not in the carousel
    const carouselListings = featuredListings.slice(0, 3);
    const nonCarouselListings = featuredListings.slice(3);
    
    // Use the middle carousel card (index 1) for the embedded checkout
    const middleCarouselCard = carouselListings[1] || carouselListings[0];
    
    // First try to find a listing with flavor text from non-carousel featured listings
    const featuredListingWithFlavor = nonCarouselListings.find(
        listing => listing?.cert?.card?.flavor_text
    ) || // If none found, try the carousel listings
    carouselListings.find(
        listing => listing?.cert?.card?.flavor_text
    );

    const handleSetSupertype = (newSupertype) => {
        setSupertypeState(newSupertype);
        setTypeState('');
        resetListings(true, newSupertype); // Pass newSupertype to resetListings
    };

    const handleSetType = (newType) => {
        setTypeState(newType);
        setSupertypeState('');
        resetListings(true, newType); // Pass newType to resetListings
    };

    if (error) return <p>Error loading cards: {error}</p>;

    return (
        <Layout>
            <Header setSupertype={handleSetSupertype} setType={handleSetType} resetListings={fetchListings} />
            {/* Gradies Banner */}
            <div className="gradies-banner">
                <p>Gradies</p>
                <h1 className="gradies-title">
                    PSA Graded Pokémon Cards
                </h1>
                <br></br>
                <br></br>
                <div className="search-bar search-bar-banner">
                    <SearchBar />
                </div>
            </div>
            {/* Carousel Section */}
            <div className="carousel-section">
                <Carousel listings={featuredListings} />
            </div>
            {/* White Pane Section */}
            <WhitePane listing={middleCarouselCard}>Shop Confidently.</WhitePane>
            <div className="white-pane-header"></div>
            <WhitePaneFooter />
        </Layout>
    );
};

export default HomePage;
